interface SpanData {
  traceId: string;
  spanId: string;
  parentSpanId: string | null;
  operationName: string;
  serviceName: string;
  serviceType: "frontend" | "lambda" | "dynamodb" | "http" | "appsync" | "aws" | "apigateway";
  startTime: number;
  endTime: number;
  duration: number;
  statusCode?: number;
  statusMessage?: string;
  httpMethod?: string;
  httpUrl?: string;
  httpStatusCode?: number;
  sessionId?: string;
  testRunId?: string;
  attributes?: Record<string, string>;
}

function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class TracingModule {
  private apiKey: string;
  private endpoint: string;
  private spanQueue: SpanData[] = [];
  private sendingSpans = false;
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXHRSend: typeof XMLHttpRequest.prototype.send | null = null;
  private failureCount = 0;
  private nextRetryTime = 0;
  private permanentlyBlocked = false;
  private spanStack: { traceId: string; spanId: string }[] = [];
  private sdkHostPattern: RegExp;
  private sessionId: string | null = null;
  private functionalTestRunId: string | null = null;
  // The module starts BEFORE session validation so the very first fetches
  // are captured; until the session ID arrives we hold flushes (bounded by
  // SESSION_WAIT_MAX_MS) so early spans don't ship without a SessionID and
  // show up as "Partial/Backend-only" correlation noise in JstVision.
  private sessionKnown = false;
  private startedAt = 0;
  private static readonly SESSION_WAIT_MAX_MS = 10_000;

  private debug: boolean;
  private tracingOrigins: (string | RegExp)[];

  constructor(apiKey: string, baseUrl: string, debug = false, tracingOrigins?: (string | RegExp)[]) {
    this.apiKey = apiKey;
    this.endpoint = `${baseUrl}/v1/tracing/spans`;
    this.sdkHostPattern = /sdkapi\./;
    this.debug = debug;
    this.tracingOrigins = tracingOrigins ?? [];
  }

  public setDebug(enabled: boolean): void {
    this.debug = enabled;
  }

  public setSessionId(id: string | null): void {
    this.sessionId = id;
    // Validation has answered (even with null); flushes may proceed.
    this.sessionKnown = true;
    if (id) {
      // Backfill spans captured before the handshake finished — these are
      // the property's earliest, most correlation-relevant requests.
      for (const span of this.spanQueue) {
        if (!span.sessionId) span.sessionId = id;
      }
    }
  }

  public setFunctionalTestContext(testRunId: string): void {
    this.functionalTestRunId = testRunId;
  }

  private log(level: "debug" | "warn" | "error", message: string, ...args: any[]): void {
    if (!this.debug && level === "debug") return;
    const prefix = `[JstVerify:Tracing:${level.toUpperCase()}]`;
    switch (level) {
      case "debug": console.debug(prefix, message, ...args); break;
      case "warn": console.warn(prefix, message, ...args); break;
      case "error": console.error(prefix, message, ...args); break;
    }
  }

  start(): void {
    this.log("debug", "Tracing started", { endpoint: this.endpoint });
    this.startedAt = Date.now();
    this.instrumentFetch();
    this.instrumentXHR();
    this.flushInterval = setInterval(() => this.flushSpans(), 5000);
  }

  /**
   * True while flushes should wait for the session handshake to answer.
   * Bounded: a page where validation never resolves still flushes after
   * SESSION_WAIT_MAX_MS rather than holding spans forever.
   */
  private waitingForSession(): boolean {
    if (this.sessionKnown) return false;
    if (!this.startedAt) return false;
    return Date.now() - this.startedAt < TracingModule.SESSION_WAIT_MAX_MS;
  }

  stop(): void {
    this.restoreFetch();
    this.restoreXHR();
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    this.flushBeacon();
  }

  flushBeacon(): void {
    if (this.spanQueue.length === 0 || this.permanentlyBlocked) return;
    const batch = this.spanQueue.splice(0, 50);
    const data = JSON.stringify({ spans: batch });
    try {
      fetch(this.endpoint, {
        method: "POST",
        body: data,
        keepalive: true,
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
      }).catch(() => {
        this.spanQueue.unshift(...batch);
      });
    } catch {
      this.spanQueue.unshift(...batch);
    }
  }

  async trace<T>(name: string, fn: () => T | Promise<T>, opts?: { attributes?: Record<string, string | number | boolean> }): Promise<T> {
    const traceId =
      this.spanStack.length > 0 ? this.spanStack[this.spanStack.length - 1].traceId : generateId();
    const parentSpanId =
      this.spanStack.length > 0 ? this.spanStack[this.spanStack.length - 1].spanId : null;
    const spanId = generateId();

    this.spanStack.push({ traceId, spanId });
    const startTime = Date.now();
    let coercedAttrs: Record<string, string> | undefined;
    if (opts?.attributes) {
      coercedAttrs = {};
      for (const [k, v] of Object.entries(opts.attributes)) {
        coercedAttrs[k] = String(v);
      }
    }

    try {
      const result = await fn();
      const endTime = Date.now();
      this.enqueueSpan({
        traceId,
        spanId,
        parentSpanId,
        operationName: name,
        serviceName: "frontend",
        serviceType: "frontend",
        startTime,
        endTime,
        duration: endTime - startTime,
        statusCode: 200,
        ...(coercedAttrs ? { attributes: coercedAttrs } : {}),
      });
      return result;
    } catch (err) {
      const endTime = Date.now();
      this.enqueueSpan({
        traceId,
        spanId,
        parentSpanId,
        operationName: name,
        serviceName: "frontend",
        serviceType: "frontend",
        startTime,
        endTime,
        duration: endTime - startTime,
        statusCode: 500,
        statusMessage: err instanceof Error ? err.message : String(err),
        ...(coercedAttrs ? { attributes: coercedAttrs } : {}),
      });
      throw err;
    } finally {
      // Targeted removal instead of blind pop — handles overlapping async trace() calls
      const idx = this.spanStack.findIndex(s => s.spanId === spanId);
      if (idx !== -1) this.spanStack.splice(idx, 1);
    }
  }

  private shouldInjectHeaders(url: string): boolean {
    // Default (no tracingOrigins configured) is same-origin-only. Returning
    // true for every origin made the SDK read the X-JstVerify-Spans relay
    // header on cross-origin responses that don't expose it via CORS, which
    // triggers the browser "Refused to get unsafe header" warning. An empty
    // tracingOrigins array now naturally yields same-origin-only, since the
    // same-origin check below still returns true and .some() on [] is false.
    try {
      const parsed = new URL(url, window.location.origin);
      const target = parsed.origin;
      if (parsed.origin === window.location.origin) return true;
      return this.tracingOrigins.some((o) =>
        o instanceof RegExp ? o.test(target) : target === o
      );
    } catch {
      return false;
    }
  }

  private isSdkRequest(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      return this.sdkHostPattern.test(parsed.hostname);
    } catch {
      return false;
    }
  }

  private getCurrentContext(): { traceId: string; spanId: string } | null {
    return this.spanStack.length > 0 ? this.spanStack[this.spanStack.length - 1] : null;
  }

  private instrumentFetch(): void {
    if (typeof window === "undefined" || !window.fetch) return;
    this.originalFetch = window.fetch;
    const self = this;

    window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const isRequest = typeof Request !== "undefined" && input instanceof Request;
      const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

      if (self.isSdkRequest(url)) {
        return self.originalFetch!.call(window, input, init);
      }

      // Capture context at call time — do NOT push/pop spanStack for async HTTP
      // calls since interleaving would corrupt the stack. spanStack is only safe
      // for the synchronous trace() API which uses try/finally.
      const ctx = self.getCurrentContext();
      const traceId = ctx ? ctx.traceId : generateId();
      const parentSpanId = ctx ? ctx.spanId : null;
      const spanId = generateId();

      // Extract method from init or Request object (Axios fetch adapter passes Request)
      const method = init?.method || (isRequest ? (input as Request).method : "GET");
      const startTime = Date.now();

      // Only inject trace headers for same-origin or explicitly allowed origins
      const injectHeaders = self.shouldInjectHeaders(url);
      let callInput: RequestInfo | URL;
      let callInit: RequestInit | undefined;

      if (injectHeaders) {
        const existingHeaders = init?.headers || (isRequest ? (input as Request).headers : undefined);
        const headers = new Headers(existingHeaders);
        headers.set("X-JstVerify-Trace-Id", traceId);
        headers.set("X-JstVerify-Parent-Span-Id", spanId);
        if (self.sessionId) headers.set("X-JstVerify-Session-Id", self.sessionId);

        if (isRequest) {
          callInput = new Request(input as Request, { ...init, headers });
          callInit = undefined;
        } else {
          callInput = input;
          callInit = { ...init, headers };
        }
      } else {
        callInput = input;
        callInit = init;
      }

      // Extract body for GraphQL operation name detection
      // Prefer init.body (string), then clone Request body if it's a GraphQL endpoint
      let bodyTextPromise: Promise<string | undefined>;
      if (typeof init?.body === "string") {
        bodyTextPromise = Promise.resolve(init.body);
      } else if (isRequest && self.isGraphQLRequest(url)) {
        try {
          bodyTextPromise = (input as Request).clone().text().catch(() => undefined);
        } catch {
          bodyTextPromise = Promise.resolve(undefined);
        }
      } else {
        bodyTextPromise = Promise.resolve(undefined);
      }

      const recordSpan = (operationName: string, response?: Response, error?: any) => {
        const endTime = Date.now();
        self.enqueueSpan({
          traceId,
          spanId,
          parentSpanId,
          operationName,
          serviceName: "frontend",
          serviceType: "http",
          startTime,
          endTime,
          duration: endTime - startTime,
          statusCode: response ? (response.ok ? 200 : response.status) : 0,
          statusMessage: !response ? (error instanceof Error ? error.message : "Network error") : undefined,
          httpMethod: method,
          httpUrl: self.truncateUrl(url),
          httpStatusCode: response?.status,
        });
      };

      return bodyTextPromise.then(bodyText => {
        const operationName = self.buildOperationName(method, url, bodyText);

        return self.originalFetch!.call(window, callInput, callInit).then(
          (response: Response) => {
            recordSpan(operationName, response);
            if (injectHeaders) {
              self.processRelaySpans(response.headers.get(TracingModule.RELAY_HEADER));
            }
            return response;
          },
          (error: any) => {
            recordSpan(operationName, undefined, error);
            throw error;
          }
        );
      });
    };
  }

  private restoreFetch(): void {
    if (this.originalFetch && typeof window !== "undefined") {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
  }

  private instrumentXHR(): void {
    if (typeof XMLHttpRequest === "undefined") return;
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest & { _jstTrace?: any },
      method: string,
      url: string | URL,
      ...args: any[]
    ) {
      const urlStr = url.toString();
      (this as any)._jstTrace = {
        method,
        url: urlStr,
        skip: self.isSdkRequest(urlStr),
      };
      return self.originalXHROpen!.apply(this, [method, url, ...args] as any);
    };

    XMLHttpRequest.prototype.send = function (
      this: XMLHttpRequest & { _jstTrace?: any },
      body?: Document | XMLHttpRequestBodyInit | null
    ) {
      const trace = (this as any)._jstTrace;
      if (!trace || trace.skip) {
        return self.originalXHRSend!.call(this, body);
      }

      const traceId =
        self.spanStack.length > 0 ? self.spanStack[self.spanStack.length - 1].traceId : generateId();
      const parentSpanId =
        self.spanStack.length > 0 ? self.spanStack[self.spanStack.length - 1].spanId : null;
      const spanId = generateId();
      const startTime = Date.now();

      // Only inject trace headers for same-origin or explicitly allowed origins
      if (self.shouldInjectHeaders(trace.url)) {
        try {
          this.setRequestHeader("X-JstVerify-Trace-Id", traceId);
          this.setRequestHeader("X-JstVerify-Parent-Span-Id", spanId);
          if (self.sessionId) this.setRequestHeader("X-JstVerify-Session-Id", self.sessionId);
        } catch {
          // Headers may already be set or request state may not allow it
        }
      }

      const operationName = self.buildOperationName(trace.method, trace.url, body as BodyInit | null | undefined);

      this.addEventListener("loadend", function () {
        const endTime = Date.now();
        self.enqueueSpan({
          traceId,
          spanId,
          parentSpanId,
          operationName,
          serviceName: "frontend",
          serviceType: "http",
          startTime,
          endTime,
          duration: endTime - startTime,
          statusCode: this.status || 0,
          httpMethod: trace.method,
          httpUrl: self.truncateUrl(trace.url),
          httpStatusCode: this.status,
        });
        if (self.shouldInjectHeaders(trace.url)) {
          try {
            self.processRelaySpans(this.getResponseHeader(TracingModule.RELAY_HEADER));
          } catch {
            // Silently ignore — header may not be accessible (CORS)
          }
        }
      });

      return self.originalXHRSend!.call(this, body);
    };
  }

  private restoreXHR(): void {
    if (typeof XMLHttpRequest === "undefined") return;
    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
      this.originalXHROpen = null;
    }
    if (this.originalXHRSend) {
      XMLHttpRequest.prototype.send = this.originalXHRSend;
      this.originalXHRSend = null;
    }
  }

  private isGraphQLRequest(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.endsWith("/graphql");
    } catch {
      return false;
    }
  }

  private extractGraphQLOperationName(body: BodyInit | null | undefined): string | null {
    if (!body || typeof body !== "string") return null;
    try {
      const parsed = JSON.parse(body);
      if (typeof parsed !== "object" || parsed === null) return null;
      if (!("query" in parsed)) return null;
      // Use explicit operationName field if present
      if (typeof parsed.operationName === "string" && parsed.operationName) {
        return parsed.operationName;
      }
      // Parse from query string: "query ListTraces" or "mutation CreateUser"
      if (typeof parsed.query === "string") {
        const match = parsed.query.match(/(?:query|mutation|subscription)\s+(\w+)/);
        if (match) return match[1];
      }
      return null;
    } catch {
      return null;
    }
  }

  private buildOperationName(method: string, url: string, body: BodyInit | null | undefined): string {
    if (method.toUpperCase() === "POST" && this.isGraphQLRequest(url)) {
      const opName = this.extractGraphQLOperationName(body);
      if (opName) return `GraphQL: ${opName}`;
      return `GraphQL: ${method} ${this.truncateUrl(url)}`;
    }
    return `${method} ${this.truncateUrl(url)}`;
  }

  private truncateUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname + (parsed.search ? parsed.search.substring(0, 50) : "");
    } catch {
      return url.substring(0, 200);
    }
  }

  private static readonly RELAY_HEADER = "X-JstVerify-Spans";
  private static readonly MAX_SPAN_QUEUE_SIZE = 200;

  private processRelaySpans(headerValue: string | null | undefined): void {
    if (!headerValue) return;
    try {
      // base64url decode (add padding back)
      const padded = headerValue + "=".repeat((4 - (headerValue.length % 4)) % 4);
      const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
      const spans: any[] = JSON.parse(binary);
      if (!Array.isArray(spans)) return;
      for (const span of spans) {
        if (
          typeof span === "object" &&
          span !== null &&
          typeof span.traceId === "string" &&
          typeof span.spanId === "string" &&
          typeof span.operationName === "string" &&
          typeof span.serviceName === "string" &&
          typeof span.startTime === "number" &&
          typeof span.endTime === "number"
        ) {
          this.enqueueSpan(span as SpanData);
        }
      }
    } catch {
      // Silently ignore malformed relay headers
    }
  }

  private enqueueSpan(span: SpanData): void {
    if (this.permanentlyBlocked) {
      this.log("debug", "Span dropped — permanently blocked");
      return;
    }
    if (this.sessionId && !span.sessionId) {
      span.sessionId = this.sessionId;
    }
    if (this.functionalTestRunId && !span.testRunId) {
      span.testRunId = this.functionalTestRunId;
    }
    if (this.spanQueue.length >= TracingModule.MAX_SPAN_QUEUE_SIZE) {
      this.spanQueue.shift();
    }
    this.spanQueue.push(span);
    if (this.spanQueue.length >= 10) {
      this.flushSpans();
    }
  }

  private async flushSpans(): Promise<void> {
    if (this.sendingSpans || this.spanQueue.length === 0 || this.permanentlyBlocked) return;
    if (this.waitingForSession()) return;
    if (Date.now() < this.nextRetryTime) return;

    this.sendingSpans = true;
    const batch = this.spanQueue.splice(0, 50);

    try {
      // Use originalFetch to avoid instrumenting our own requests
      const fetchFn = this.originalFetch || window.fetch;
      const response = await fetchFn.call(window, this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify({ spans: batch }),
      });

      if (response.ok) {
        this.failureCount = 0;
        this.nextRetryTime = 0;
        this.log("debug", `Flushed ${batch.length} spans`);
      } else if (response.status === 403) {
        this.permanentlyBlocked = true;
        this.log("warn", "Span endpoint returned 403 — tracing permanently disabled. Check API key and property config.");
      } else {
        // Put spans back for retry
        this.spanQueue.unshift(...batch);
        this.recordRetryDelay();
        this.log("warn", "Span flush failed", response.status);
      }
    } catch (err) {
      this.spanQueue.unshift(...batch);
      this.recordRetryDelay();
      this.log("error", "Span flush network error", err);
    } finally {
      this.sendingSpans = false;
    }
  }

  private recordRetryDelay(): void {
    this.failureCount++;
    const baseDelay = Math.min(1000 * Math.pow(2, this.failureCount), 300000);
    const jitter = baseDelay * 0.25 * Math.random();
    this.nextRetryTime = Date.now() + baseDelay + jitter;
  }
}
